import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { API, mediaUrl } from "../api";

const ASPECTS = [["free", "Original"], ["1:1", "Square"], ["4:5", "Portrait"], ["3:2", "Landscape"], ["16:9", "Wide"]];
const ratioOf = (a, img) => a === "free" ? (img ? img.width / img.height : 1.5) : a.split(":").reduce((w, h) => Number(w) / Number(h));
const NO_CROP = /\.(svg|gif)$/i;

export async function uploadImage(file) {
  const fd = new FormData(); fd.append("file", file);
  let r;
  try { r = await fetch(`${API}/admin/media`, { method: "POST", credentials: "include", body: fd }); }
  catch { throw new Error("Upload failed: no connection to the server. Check your internet and try again."); }
  if (!r.ok) {
    let msg = `Upload failed (HTTP ${r.status}).`;
    try { msg = (await r.json()).detail || msg; } catch {}
    if (r.status === 401) msg = "Your owner session expired. Sign in again, then retry the upload.";
    if (r.status === 413) msg = "That image is too large for the server. Please use an image under 8 MB.";
    throw new Error(msg);
  }
  return r.json();
}

// Canvas cropper: drag to reposition, zoom slider, aspect presets, live preview.
function Cropper({ file, onApply, onCancel }) {
  const [img, setImg] = useState(null);
  const [aspect, setAspect] = useState("free");
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const W = Math.min(520, window.innerWidth - 56);
  const H = Math.round(W / ratioOf(aspect, img));

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => setImg(i); i.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => { setOff({ x: 0, y: 0 }); setZoom(1); }, [aspect]);

  const geometry = useCallback((w, h) => {
    const base = Math.max(w / img.width, h / img.height) * zoom;
    const dw = img.width * base, dh = img.height * base;
    const maxX = Math.max(0, (dw - w) / 2), maxY = Math.max(0, (dh - h) / 2);
    const s = w / W;
    const ox = Math.max(-maxX, Math.min(maxX, off.x * s)), oy = Math.max(-maxY, Math.min(maxY, off.y * s));
    return { dw, dh, x: (w - dw) / 2 + ox, y: (h - dh) / 2 + oy };
  }, [img, zoom, off, W]);

  useEffect(() => {
    if (!img || !canvasRef.current) return;
    const c = canvasRef.current, ctx = c.getContext("2d");
    c.width = W; c.height = H;
    ctx.fillStyle = "#111"; ctx.fillRect(0, 0, W, H);
    const g = geometry(W, H);
    ctx.drawImage(img, g.x, g.y, g.dw, g.dh);
  }, [img, W, H, geometry]);

  const onDown = (e) => { drag.current = { x: e.clientX - off.x, y: e.clientY - off.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onMove = (e) => { if (drag.current) setOff({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onUp = () => { drag.current = null; };

  const apply = () => {
    const outW = Math.min(1600, img.width * (aspect === "free" ? 1 : Math.min(1, 1600 / img.width)));
    const outH = Math.round(outW / ratioOf(aspect, img));
    const c = document.createElement("canvas"); c.width = Math.round(outW); c.height = outH;
    const ctx = c.getContext("2d");
    const g = geometry(c.width, c.height);
    ctx.drawImage(img, g.x, g.y, g.dw, g.dh);
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    c.toBlob((blob) => {
      if (!blob) { toast.error("Could not process this image. Try a different file."); return; }
      const name = file.name.replace(/\.[^.]+$/, "") + (type === "image/png" ? ".png" : ".jpg");
      onApply(new File([blob], name, { type }));
    }, type, 0.9);
  };

  if (!img) return <p className="muted small">Loading image…</p>;
  return (
    <div className="img-crop" data-testid="image-cropper">
      <div className="img-crop-aspects">{ASPECTS.map(([k, l]) => <button key={k} type="button" className={aspect === k ? "active" : ""} onClick={() => setAspect(k)} data-testid={`aspect-${k.replace(":", "x")}`}>{l}</button>)}</div>
      <canvas ref={canvasRef} className="img-crop-canvas" style={{ width: W, height: H, touchAction: "none" }} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="crop-canvas" />
      <p className="tiny muted">Drag the photo to reposition it inside the frame.</p>
      <label className="img-crop-zoom">Zoom <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="crop-zoom" /></label>
      <div className="row between">
        <button type="button" className="button secondary small" onClick={onCancel} data-testid="crop-cancel">Back</button>
        <div className="row">
          <button type="button" className="button secondary small" onClick={() => onApply(file)} data-testid="crop-use-original">Use original</button>
          <button type="button" className="button small" onClick={apply} data-testid="crop-apply">Apply crop</button>
        </div>
      </div>
    </div>
  );
}

// Modal: current image, upload (with crop), choose from library, remove.
export default function ImageTool({ target, media, onDone, onClose, onMediaChange }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pending, setPending] = useState(null); // {url, file} awaiting confirm
  const [alt, setAlt] = useState(target.alt || "");
  const fileRef = useRef(null);
  const altField = (<div className="field" style={{ marginTop: 4 }}><label>Short description (alt text)</label>
    <input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={200} placeholder="For example: Michael smiling in a navy blazer" data-testid="image-alt-input" />
    <p className="tiny muted">Read aloud by screen readers and used by search engines. Describe what is in the photo.</p></div>);
  const pick = (f) => {
    if (!f) return;
    setErr("");
    if (!/^image\//.test(f.type)) { setErr("That file is not an image. Choose a PNG, JPG, WEBP, SVG, or GIF."); return; }
    if (f.size > 8 * 1024 * 1024) { setErr("This image is over 8 MB. Please pick a smaller image or crop it first."); return; }
    if (NO_CROP.test(f.name)) setPending({ file: f, url: URL.createObjectURL(f) }); else setFile(f);
  };
  const confirm = async () => {
    setBusy(true); setErr("");
    try {
      const r = await uploadImage(pending.file);
      await onMediaChange?.();
      onDone(r.url, alt);
      toast.success("Image uploaded and placed. Save or publish to keep it.");
    } catch (e) { setErr(e.message); toast.error(e.message); }
    finally { setBusy(false); }
  };
  const current = target.current ? (mediaUrl(target.current) || target.current) : "";
  return (
    <div className="img-modal-back" onClick={onClose} data-testid="image-tool">
      <div className="img-modal" onClick={(e) => e.stopPropagation()}>
        <div className="row between"><h3>{target.label || "Image"}</h3><button type="button" className="text-button" onClick={onClose} data-testid="image-tool-close">Close</button></div>
        {err && <div className="banner" role="alert" data-testid="image-tool-error">{err}</div>}
        {file ? <Cropper file={file} onCancel={() => setFile(null)} onApply={(f) => { setFile(null); setPending({ file: f, url: URL.createObjectURL(f) }); }} /> : pending ? (
          <div className="stack">
            <p className="small muted">Preview: this is how the image will look once placed.</p>
            <img src={pending.url} alt="Preview" className="img-preview" data-testid="image-preview" />
            {altField}
            <div className="row between">
              <button type="button" className="button secondary small" onClick={() => { setPending(null); fileRef.current.value = ""; }} disabled={busy}>Choose another</button>
              <button type="button" className="button small" onClick={confirm} disabled={busy} data-testid="image-confirm">{busy ? "Uploading…" : "Use this image"}</button>
            </div>
          </div>
        ) : (
          <div className="stack">
            {current ? <img src={current} alt="Current" className="img-preview" data-testid="image-current" /> : <div className="img-empty">No image here yet</div>}
            {current && <>{altField}<div className="row"><button type="button" className="button secondary small" onClick={() => onDone(target.current, alt)} data-testid="image-save-alt">Save description</button></div></>}
            <div className="row">
              <button type="button" className="button small" onClick={() => fileRef.current.click()} data-testid="image-upload-btn">{current ? "Replace / crop a new photo" : "Upload a photo"}</button>
              {current && target.allowRemove !== false && <button type="button" className="button secondary small danger" onClick={() => { onDone("", ""); }} data-testid="image-remove-btn">Remove</button>}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" hidden onChange={(e) => pick(e.target.files[0])} data-testid="media-file-input" />
            {media?.length ? <><p className="small muted">Or pick from your media library</p>
              <div className="media-grid">{media.map((m) => <img key={m.id} src={mediaUrl(m.url)} alt={m.alt || m.original} title={m.original} onClick={() => onDone(m.url, alt || m.alt || "")} data-testid={`library-${m.id}`} />)}</div></> : null}
          </div>
        )}
      </div>
    </div>
  );
}
